import React, { ReactElement, useLayoutEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { matchPath, NavLink, useLocation } from 'react-router-dom';

import { Tab } from '../Tab/Tab';

import { ITabProps, ITabsProps } from './tabs.interface';
import styles from './tabs.module.scss';

import { ArrowDown, Menu } from 'views/components';

const Tabs = (props: ITabsProps): ReactElement => {
  const { children, currentId, onTabChange, className } = props;
  const location = useLocation();
  const intl = useIntl();

  const items = useMemo(
    () => React.Children.toArray(children) as ReactElement<ITabProps>[],
    [children],
  );

  const wrapperRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef(new Map<string, HTMLLIElement>());
  const moreMeasureRef = useRef<HTMLLIElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const measure = measureRef.current;
    if (!wrapper || !measure) {
      return undefined;
    }

    const recalculate = (): void => {
      const availableWidth = wrapper.clientWidth;
      const itemGap = Number.parseFloat(window.getComputedStyle(measure).columnGap) || 0;
      const moreButtonWidth = moreMeasureRef.current?.offsetWidth ?? 0;
      const itemWidths = items.map(
        item => itemRefs.current.get(item.props.id)?.offsetWidth ?? 0,
      );

      const totalWidthAllItems =
        itemWidths.reduce((sum, width) => sum + width, 0) +
        itemGap * Math.max(0, itemWidths.length - 1);

      if (totalWidthAllItems <= availableWidth) {
        setVisibleCount(items.length);
        return;
      }

      let usedWidth = 0;
      let nextVisibleCount = 0;

      for (let index = 0; index < itemWidths.length; index += 1) {
        const prefixGap = index > 0 ? itemGap : 0;
        const widthWithItem = usedWidth + prefixGap + itemWidths[index];

        if (widthWithItem + itemGap + moreButtonWidth > availableWidth) {
          break;
        }

        usedWidth = widthWithItem;
        nextVisibleCount = index + 1;
      }

      setVisibleCount(nextVisibleCount);
    };

    recalculate();

    let frameId: number | null = null;
    // A ResizeObserver callback that itself triggers a resize (here: hiding/
    // showing items changes the row's content, which can change its own
    // box) can retrigger synchronously within the same observation cycle —
    // that's what the browser's "ResizeObserver loop completed with
    // undelivered notifications" error is warning about. Deferring the
    // actual recalculation to the next frame breaks that synchronous chain.
    const observer = new ResizeObserver(() => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(recalculate);
    });
    observer.observe(wrapper);
    observer.observe(measure);
    itemRefs.current.forEach(item => observer.observe(item));
    if (moreMeasureRef.current) observer.observe(moreMeasureRef.current);

    return () => {
      observer.disconnect();
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [items]);

  const isItemActive = (item: ReactElement<ITabProps>): boolean =>
    item.props.to
      ? Boolean(matchPath(location.pathname, { path: item.props.to }))
      : currentId === item.props.id;

  const setItemRef =
    (id: string) =>
    (el: HTMLLIElement | null): void => {
      if (el) {
        itemRefs.current.set(id, el);
      } else {
        itemRefs.current.delete(id);
      }
    };

  const visibleItems = items.slice(0, visibleCount);
  const hiddenItems = items.slice(visibleCount);
  const isMoreActive = hiddenItems.some(isItemActive);
  const moreLabel = intl.formatMessage({ id: 'tabs.btn.more' });

  return (
    <div className={classNames(styles.wrapper, className)} ref={wrapperRef}>
      <ul className={styles.measure} aria-hidden="true" ref={measureRef}>
        {items.map(item => (
          <Tab
            key={item.props.id}
            {...item.props}
            isMeasurement
            ref={setItemRef(item.props.id)}
          />
        ))}
        <li className={styles.item} ref={moreMeasureRef}>
          <span className={styles.moreButtonInner}>
            {moreLabel}
            <ArrowDown size="16px" color="currentColor" />
          </span>
          <div className={styles.moreButtonUnderline} />
        </li>
      </ul>

      <ul className={styles.list} role="tablist">
        {visibleItems.map(item => (
          <Tab
            key={item.props.id}
            {...item.props}
            isActive={isItemActive(item)}
            onClick={() => onTabChange?.(item.props.id)}
          />
        ))}
        {hiddenItems.length > 0 && (
          <li className={classNames(styles.item, styles.moreItem)}>
            <button
              type="button"
              className={styles.moreButton}
              ref={moreButtonRef}
              onClick={() => setIsMoreOpen(open => !open)}
              aria-haspopup="menu"
              aria-expanded={isMoreOpen}
              aria-label={moreLabel}
            >
              <span
                className={classNames(styles.moreButtonInner, {
                  [styles.moreButtonActive]: isMoreActive,
                })}
              >
                {moreLabel}
                <ArrowDown size="16px" color="currentColor" />
              </span>
              <div className={styles.moreButtonUnderline} />
            </button>
            {isMoreOpen && (
              <Menu
                anchorEl={moreButtonRef.current}
                onClose={() => setIsMoreOpen(false)}
                placement="bottom-end"
                aria-label={moreLabel}
              >
                {hiddenItems.map(item => (
                  <Menu.Item key={item.props.id} onClick={() => setIsMoreOpen(false)}>
                    {item.props.to ? (
                      <NavLink
                        to={item.props.to}
                        className={styles.moreMenuLink}
                        activeClassName={styles.moreMenuLinkActive}
                      >
                        {item.props.children}
                      </NavLink>
                    ) : (
                      <button
                        type="button"
                        className={styles.moreMenuButton}
                        onClick={() => onTabChange?.(item.props.id)}
                      >
                        {item.props.children}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu>
            )}
          </li>
        )}
      </ul>
    </div>
  );
};

export { Tabs };
